import toast from 'react-hot-toast';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { ErrorResponse, Organization, PaginatedUsers } from '@components/pages/Organization/types';
import {
  CorporateSubscriptionResponse,
  UpdateCorporateSubscriptionParams,
  updateCorporateSubscriptionAccessLevel,
} from '@utils/backend-api/corporateSubscriptions';
import { ACTIVE_USERS_KEY, ORGANIZATION_DETAILS_KEY } from '@utils/backend-api/organizations';
import { calculateSeatAvailability } from '@utils/corporateSubscriptionHelpers';

const useCorporateSubscriptionAccessLevelUpdate = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, data, isLoading } = useMutation(
    (params: UpdateCorporateSubscriptionParams) => updateCorporateSubscriptionAccessLevel(params),
    {
      onSuccess: ({ user: updatedUser, message }: CorporateSubscriptionResponse) => {
        toast.success(message);

        queryClient.setQueryData<Organization>(ORGANIZATION_DETAILS_KEY, (oldData) => {
          if (!oldData) return oldData;

          const { accessLevel } = updatedUser;
          const { availableProSeats, availableStandardSeats } = calculateSeatAvailability(
            accessLevel,
            oldData.availableProSeats,
            oldData.availableStandardSeats,
            oldData.totalProSeats,
            oldData.totalStandardSeats
          );

          return {
            ...oldData,
            availableProSeats,
            availableStandardSeats,
          };
        });
        queryClient.setQueryData([ACTIVE_USERS_KEY], (oldData: InfiniteData<PaginatedUsers>) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              return {
                ...page,
                users: page.users.map((existingUser) =>
                  existingUser.id === updatedUser.id ? updatedUser : existingUser
                ),
              };
            }),
          };
        });
      },
      onError: (e: ErrorResponse) => {
        toast.error(e.message);
      },
    }
  );

  return { mutate, mutateAsync, data, isLoading };
};

export default useCorporateSubscriptionAccessLevelUpdate;
